import React, { useState, useEffect } from 'react';
import { X, Package, Calendar, Clock, AlertCircle, Loader2, Archive } from 'lucide-react';
import { fetchCustomerOrders } from '../lib/api';
import type { Customer, Order } from '../lib/types';

interface OrderHistoryProps {
  customerId: string;
  customer?: Customer;
  onClose: () => void;
}

export const OrderHistory: React.FC<OrderHistoryProps> = ({ customerId, customer, onClose }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, [customerId]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await fetchCustomerOrders(customerId);
      setOrders(data);
      setError(null);
    } catch (err) {
      setError('Failed to load order history');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  const calculateOrderTotal = (order: Order): number => {
    return order.items.reduce((total, item) => {
      const itemTotal = item.priceAtTime * item.quantity;
      const discount = itemTotal * (item.discountPercentage / 100);
      return total + (itemTotal - discount);
    }, 0);
  };

  const getStatusColor = (status: Order['status'], archived: boolean): string => {
    if (archived) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'fulfillment':
        return 'bg-purple-100 text-purple-800';
      case 'pending_delivery':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Order History - {customer?.companyName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Showing all orders including archived
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600">{error}</p>
              <button
                onClick={loadOrders}
                className="mt-4 text-green-600 hover:text-green-700"
              >
                Try Again
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No orders found for this customer</p>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div 
                  key={order.id}
                  className={`bg-white rounded-lg border ${
                    order.archived ? 'border-gray-200 bg-gray-50' : 'border-gray-200'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-medium text-gray-900">
                            Order #{order.id}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getStatusColor(order.status, order.archived)
                          }`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace('_', ' ')}
                          </span>
                          {order.archived && (
                            <span className="flex items-center text-xs text-gray-500">
                              <Archive className="w-3 h-3 mr-1" />
                              Archived {new Date(order.archivedAt!).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-500 space-x-4">
                          <span className="flex items-center inline-block">
                            <Calendar className="w-4 h-4 mr-1" />
                            Created: {new Date(order.createdAt).toLocaleDateString()}
                          </span>
                          {order.completedAt && (
                            <span className="flex items-center inline-block">
                              <Clock className="w-4 h-4 mr-1" />
                              Completed: {new Date(order.completedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-semibold text-green-600">
                          {formatCurrency(calculateOrderTotal(order))}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Product
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quantity
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Price
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Discount
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {order.items.map((item) => {
                            const itemTotal = item.priceAtTime * item.quantity;
                            const discount = itemTotal * (item.discountPercentage / 100);
                            const total = itemTotal - discount;

                            return (
                              <tr key={item.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {item.product.product}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    SKU: {item.product.sku}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                                  {item.quantity}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                                  {formatCurrency(item.priceAtTime)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                                  {item.discountPercentage}%
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                                  {formatCurrency(total)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {order.notes && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600">{order.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};